import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

interface LinkEmbedProps {
  url: string;
  className?: string;
  /** Hide the thumbnail image (e.g. when a cover image is already displayed above). */
  hideImage?: boolean;
}

interface OgData {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
}

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Fetch and parse OpenGraph / meta tags from a URL via the CORS proxy. */
async function fetchOgData(url: string, signal: AbortSignal): Promise<OgData> {
  const proxied = CORS_PROXY + encodeURIComponent(url);
  const res = await fetch(proxied, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const html = await res.text();

  // Parse just the <head> portion for speed — full DOM parse is expensive
  const headMatch = html.match(/<head[\s\S]*?<\/head>/i);
  const head = headMatch?.[0] ?? html.slice(0, 8000);

  const getMeta = (property: string): string | undefined => {
    // og: and twitter: properties
    const ogMatch = head.match(
      new RegExp(
        `<meta[^>]+(?:property|name)\\s*=\\s*["']${property}["'][^>]+content\\s*=\\s*["']([^"']*?)["']`,
        'i',
      ),
    ) ?? head.match(
      new RegExp(
        `<meta[^>]+content\\s*=\\s*["']([^"']*?)["'][^>]+(?:property|name)\\s*=\\s*["']${property}["']`,
        'i',
      ),
    );
    return ogMatch?.[1]?.trim() || undefined;
  };

  const title =
    getMeta('og:title') ??
    getMeta('twitter:title') ??
    head.match(/<title[^>]*>([^<]*)<\/title>/i)?.[1]?.trim();

  const description =
    getMeta('og:description') ??
    getMeta('twitter:description') ??
    getMeta('description');

  const image =
    getMeta('og:image') ??
    getMeta('twitter:image') ??
    getMeta('twitter:image:src');

  const siteName = getMeta('og:site_name');

  // Resolve relative image URLs to absolute
  let resolvedImage = image;
  if (image) {
    try {
      resolvedImage = new URL(image, url).href;
    } catch {
      resolvedImage = undefined;
    }
  }

  // Try to get the favicon
  let favicon: string | undefined;
  try {
    const origin = new URL(url).origin;
    favicon = `${origin}/favicon.ico`;
  } catch {
    // skip
  }

  return {
    title: title ? decodeHtmlEntities(title) : undefined,
    description: description ? decodeHtmlEntities(description) : undefined,
    image: resolvedImage,
    siteName: siteName ? decodeHtmlEntities(siteName) : undefined,
    favicon,
  };
}

function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)));
}

/** Hook that fetches OG data for a URL, cached for 10 minutes. */
function useLinkPreview(url: string) {
  return useQuery<OgData>({
    queryKey: ['link-preview', url],
    queryFn: ({ signal }) => fetchOgData(url, signal),
    staleTime: 10 * 60 * 1000,
    retry: 1,
    // Don't refetch on window focus for previews
    refetchOnWindowFocus: false,
  });
}

/**
 * Rich link preview card — fetches OpenGraph metadata via CORS proxy
 * and renders title, description, image, and site name.
 */
export function LinkEmbed({ url, className, hideImage }: LinkEmbedProps) {
  const { data: og, isLoading, isError } = useLinkPreview(url);

  let hostname = url;
  try {
    hostname = new URL(url).hostname.replace(/^www\./, '');
  } catch { /* keep raw */ }

  const displayName = og?.siteName || hostname;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'block rounded-xl overflow-hidden no-underline transition-opacity hover:opacity-90',
        className,
      )}
      style={{
        border: '1px solid var(--gb-border)',
        background: 'var(--bg-secondary)',
      }}
    >
      {/* Image */}
      {!hideImage && og?.image && (
        <div
          className="w-full overflow-hidden"
          style={{ maxHeight: 220, background: 'var(--bg-primary)' }}
        >
          <img
            src={og.image}
            alt={og.title ?? ''}
            className="w-full object-cover"
            style={{ maxHeight: 220 }}
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}

      {/* Text content */}
      <div className="px-3 py-2.5">
        {/* Site name row */}
        <div className="flex items-center gap-1.5 mb-1">
          {og?.favicon && (
            <img
              src={og.favicon}
              alt=""
              width={12}
              height={12}
              className="rounded-sm shrink-0"
              style={{ opacity: 0.7 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <span
            className="text-xs font-medium uppercase tracking-wide truncate"
            style={{ color: 'var(--text-secondary)' }}
          >
            {isLoading ? <SkeletonLine width={80} /> : displayName}
          </span>
        </div>

        {/* Title */}
        <div
          className="text-sm font-semibold leading-snug line-clamp-2"
          style={{ color: 'var(--text-primary)' }}
        >
          {isLoading ? (
            <SkeletonLine width={240} />
          ) : isError ? (
            <span className="truncate">{hostname}</span>
          ) : (
            og?.title ?? hostname
          )}
        </div>

        {/* Description */}
        {!isLoading && !isError && og?.description && (
          <p
            className="text-xs mt-1 line-clamp-2 leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
          >
            {og.description}
          </p>
        )}

        {/* URL fallback when no title */}
        {!isLoading && (isError || !og?.title) && (
          <p
            className="text-xs mt-0.5 truncate"
            style={{ color: 'var(--text-secondary)' }}
          >
            {url}
          </p>
        )}
      </div>
    </a>
  );
}

function SkeletonLine({ width }: { width: number }) {
  return (
    <span
      className="inline-block rounded animate-pulse"
      style={{
        width,
        height: '0.75em',
        background: 'var(--gb-border)',
        verticalAlign: 'middle',
      }}
    />
  );
}
